import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useCurrency } from '../../context/CurrencyContext';
import { useMemorialOwner } from '../../hooks/useMemorialOwner';
import { writeFetch } from '../../api';
import DonateInMemoryModal from './DonateInMemoryModal';
import DonationCampaignForm from '../Donations/DonationCampaignForm';
import DonationImpactUpdates from '../Donations/DonationImpactUpdates';

const {
  FiGift, FiHeart, FiCheck, FiUsers, FiArrowRight,
  FiShield, FiX, FiPieChart, FiChevronDown, FiChevronUp,
  FiActivity, FiPlus, FiTrendingUp,
} = FiIcons;

const CAMPAIGNS_TABLE = 'donation_campaigns_1776500000';
const MONET_TABLE     = 'monetization_config_1776389352234';
const NCB_URL         = 'https://app.nocodebackend.com/api/data';
const NCB_INSTANCE    = '53179_memorial_sanctuary';
const NCB_SECRET      = '8538d058b9882f511ddf0d39c7d91bc6027abeeb4b8b69523ca8a6d1fdef';

function ncbHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${NCB_SECRET}`,
    'X-Database-Instance': NCB_INSTANCE,
  };
}

function ncbUrl(path) {
  const sep = path.includes('?') ? '&' : '?';
  return `${NCB_URL}/${path}${sep}Instance=${NCB_INSTANCE}`;
}

const MOCK_DONORS = [
  { id: 1, name: 'Sarah Jenkins',     amount: 150,  date: '2 hours ago',  avatar: 'https://i.pravatar.cc/100?img=11' },
  { id: 2, name: 'David Thompson',    amount: 500,  date: '5 hours ago',  avatar: 'https://i.pravatar.cc/100?img=12' },
  { id: 3, name: 'Anonymous',         amount: 50,   date: '1 day ago',    avatar: 'https://i.pravatar.cc/100?img=13' },
  { id: 4, name: 'The Miller Family', amount: 1000, date: '2 days ago',   avatar: 'https://i.pravatar.cc/100?img=14' },
  { id: 5, name: 'Robert Chen',       amount: 200,  date: '3 days ago',   avatar: 'https://i.pravatar.cc/100?img=15' },
];

/* Fetch platform fee + tip options from monetization config */
async function fetchDonationConfig() {
  try {
    const res  = await fetch(ncbUrl(`read/${MONET_TABLE}?category=donations&limit=20`), { headers: ncbHeaders() });
    const data = await res.json();
    const rows = Array.isArray(data.data) ? data.data : [];
    const get  = (key, def) => {
      const r = rows.find(r => r.config_key === key);
      return r ? r.config_value : def;
    };
    return {
      feeRate:    parseFloat(get('donation_platform_fee_pct', '10')) / 100,
      tipOptions: [
        parseInt(get('donation_tip_option_1_pct', '1')),
        parseInt(get('donation_tip_option_2_pct', '5')),
        parseInt(get('donation_tip_option_3_pct', '10')),
      ],
      allowTip:   get('donation_allow_donor_tip', 'true') === 'true',
    };
  } catch {
    return { feeRate: 0.10, tipOptions: [1, 5, 10], allowTip: true };
  }
}

const DonationProgressWidget = ({ memorial }) => {
  const { formatCurrency } = useCurrency();
  const { isOwner }        = useMemorialOwner(memorial);

  const [showDonateModal,   setShowDonateModal]   = useState(false);
  const [showDonorsModal,   setShowDonorsModal]   = useState(false);
  const [showBreakdown,     setShowBreakdown]     = useState(false);
  const [showSuccess,       setShowSuccess]       = useState(false);
  const [showCampaignForm,  setShowCampaignForm]  = useState(false);
  const [lastDonation,      setLastDonation]      = useState(null);
  const [donationConfig,    setDonationConfig]    = useState({ feeRate: 0.10, tipOptions: [1, 5, 10], allowTip: true });
  const [campaigns,         setCampaigns]         = useState([]);
  const [activeCampaign,    setActiveCampaign]    = useState(null);

  useEffect(() => {
    fetchDonationConfig().then(setDonationConfig);
    loadCampaigns();
  }, [memorial?.id]);

  const loadCampaigns = useCallback(async () => {
    if (!memorial?.id) return;
    try {
      const url  = ncbUrl(`read/${CAMPAIGNS_TABLE}?memorial_id=${memorial.id}&status=active&limit=5`);
      const res  = await fetch(url, { headers: ncbHeaders() });
      const data = await res.json();
      const rows = Array.isArray(data.data) ? data.data : [];
      setCampaigns(rows);
      if (rows.length > 0) setActiveCampaign(rows[0]);
    } catch { /* silent */ }
  }, [memorial?.id]);

  const { raised, goal, charity } = memorial?.donations || { raised: 0, goal: 0, charity: 'Memorial Fund' };
  const effectiveRaised  = activeCampaign ? Number(activeCampaign.raised_amount || 0) : raised;
  const effectiveGoal    = activeCampaign ? Number(activeCampaign.goal_amount || goal) : goal;
  const effectiveCharity = activeCampaign?.beneficiary_name || charity;
  const percentage       = effectiveGoal > 0 ? Math.min(Math.round((effectiveRaised / effectiveGoal) * 100), 100) : 0;
  const totalFees        = effectiveRaised * donationConfig.feeRate;
  const netAmount        = effectiveRaised - totalFees;

  const handleDonationSuccess = (data) => {
    setLastDonation(data);
    setShowDonateModal(false);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 5000);
    loadCampaigns();
  };

  return (
    <>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-sm border border-brand-dark overflow-hidden hover:shadow-2xl hover:border-accent-gold/30 transition-all duration-500">

        {/* Header */}
        <div className="relative p-8 pb-0">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-accent-gold/10 rounded-2xl flex items-center justify-center text-accent-gold shadow-sm border border-accent-gold/20">
                <SafeIcon icon={FiGift} className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-serif text-2xl text-accent-navy leading-none mb-1">Legacy Gift</h3>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-accent-gold">
                  Honouring {memorial?.name?.split(' ')[0] || 'your loved one'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-serif text-accent-navy">{formatCurrency(effectiveRaised)}</div>
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">
                Target: {formatCurrency(effectiveGoal)}
              </p>
            </div>
          </div>

          {/* Campaign selector */}
          {campaigns.length > 1 && (
            <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
              {campaigns.map(c => (
                <button key={c.id} onClick={() => setActiveCampaign(c)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border-2 transition-all ${
                    activeCampaign?.id === c.id ? 'bg-accent-navy text-white border-accent-navy' : 'border-brand-dark text-text-muted'
                  }`}>
                  {c.title?.slice(0, 20)}…
                </button>
              ))}
            </div>
          )}

          {/* Progress bar */}
          <div className="relative py-4">
            <div className="h-4 bg-brand-light rounded-full overflow-hidden border border-brand-dark p-0.5">
              <motion.div initial={{ width: 0 }} animate={{ width: `${percentage}%` }}
                transition={{ duration: 2, ease: 'circOut' }}
                className="h-full bg-accent-gold rounded-full shadow-[0_0_15px_rgba(197,168,128,0.3)]" />
            </div>
            <div className="flex justify-between items-center mt-3">
              <span className="text-[11px] font-black uppercase tracking-widest text-accent-gold">{percentage}% Reached</span>
              <div className="flex items-center gap-1.5 text-text-muted">
                <SafeIcon icon={FiUsers} className="w-3.5 h-3.5" />
                <span className="text-[11px] font-bold">{activeCampaign?.donor_count || 42} Supporters</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-8 pt-6 space-y-6">
          {/* Financial Transparency */}
          <div className="bg-brand-light/20 border border-brand-dark rounded-3xl overflow-hidden">
            <button onClick={() => setShowBreakdown(!showBreakdown)}
              className="w-full p-4 flex items-center justify-between text-left group">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white rounded-xl border border-brand-dark flex items-center justify-center text-accent-navy shadow-sm group-hover:text-accent-gold transition-colors">
                  <SafeIcon icon={FiPieChart} className="w-4 h-4" />
                </div>
                <span className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Financial Transparency</span>
              </div>
              <SafeIcon icon={showBreakdown ? FiChevronUp : FiChevronDown} className="text-text-muted w-4 h-4" />
            </button>
            <AnimatePresence>
              {showBreakdown && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                  className="px-4 pb-4 overflow-hidden">
                  <div className="pt-2 space-y-3 border-t border-brand-dark/50">
                    <div className="flex justify-between items-center">
                      <span className="text-[11px] font-bold text-text-muted">Gross Amount Raised</span>
                      <span className="text-[11px] font-black text-accent-navy">{formatCurrency(effectiveRaised)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[11px] font-bold text-text-muted">Platform Fee ({(donationConfig.feeRate * 100).toFixed(0)}%)</span>
                      <span className="text-[11px] font-black text-red-500">-{formatCurrency(totalFees)}</span>
                    </div>
                    <div className="pt-2 border-t border-brand-dark flex justify-between items-center">
                      <span className="text-[11px] font-black uppercase tracking-widest text-accent-navy">Net to Charity</span>
                      <div className="text-right">
                        <span className="text-[13px] font-serif font-black text-accent-gold">{formatCurrency(netAmount)}</span>
                        <p className="text-[8px] font-black text-text-muted uppercase tracking-tighter">Directed to {effectiveCharity}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Charity info */}
          <div className="bg-brand-light/50 border border-brand-dark p-5 rounded-3xl flex items-start gap-4">
            <div className="w-10 h-10 bg-white rounded-xl border border-brand-dark flex flex-shrink-0 items-center justify-center text-accent-navy shadow-sm">
              <SafeIcon icon={FiActivity} className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-bold text-accent-navy uppercase tracking-wider mb-1">Impact Goal</p>
              <p className="text-[13px] text-text-muted leading-relaxed">
                Your support helps <span className="font-bold text-accent-navy">{effectiveCharity}</span> continue their vital work in memory of {memorial?.name?.split(' ')[0] || 'your loved one'}.
              </p>
            </div>
          </div>

          <DonationImpactUpdates campaign={activeCampaign} memorial={memorial} canManage={isOwner} />

          {/* GIVE CTA */}
          <button onClick={() => setShowDonateModal(true)} disabled={showSuccess}
            className="w-full btn-primary py-5 text-base flex items-center justify-center gap-3 group relative overflow-hidden">
            <AnimatePresence mode="wait">
              {showSuccess ? (
                <motion.div key="success" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-2">
                  <SafeIcon icon={FiCheck} />
                  <span className="text-xs font-black uppercase tracking-widest">
                    Thank You{lastDonation?.donorName !== 'Anonymous' && lastDonation?.donorName ? `, ${lastDonation.donorName.split(' ')[0]}` : ''}!
                  </span>
                </motion.div>
              ) : (
                <motion.div key="default" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2">
                  <SafeIcon icon={FiHeart} className="group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-black uppercase tracking-[0.15em]">GIVE TO THIS LEGACY</span>
                </motion.div>
              )}
            </AnimatePresence>
          </button>

          {/* Owner: create campaign button — visible only to the verified memorial owner */}
          {isOwner && (
            <button
              type="button"
              onClick={() => setShowCampaignForm(true)}
              className="w-full flex items-center justify-center gap-2 py-3 border-2 border-accent-navy text-accent-navy rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy hover:text-white transition-all">
              <SafeIcon icon={FiPlus} className="w-3.5 h-3.5" />
              {campaigns.length > 0 ? 'Add Another Campaign' : 'Create Donation Campaign'}
            </button>
          )}

          {/* Social proof */}
          <div className="pt-6 border-t border-brand-light flex items-center justify-between">
            <div className="flex -space-x-3">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="w-10 h-10 rounded-full border-4 border-white bg-brand-dark flex items-center justify-center shadow-sm overflow-hidden">
                  <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="Donor" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-300" />
                </div>
              ))}
              <div className="w-10 h-10 rounded-full border-4 border-white bg-accent-gold text-white flex items-center justify-center text-[10px] font-black shadow-sm">+38</div>
            </div>
            <button onClick={() => setShowDonorsModal(true)}
              className="text-[10px] font-black uppercase tracking-widest text-text-muted hover:text-accent-gold transition-colors flex items-center gap-1.5 group/link">
              See all <SafeIcon icon={FiArrowRight} className="w-3 h-3 group-hover/link:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>

        {/* Trust footer */}
        <div className="bg-brand-light px-8 py-4 flex items-center justify-center gap-2">
          <SafeIcon icon={FiShield} className="w-3.5 h-3.5 text-accent-gold" />
          <span className="text-[9px] font-black uppercase tracking-[0.2em] text-text-muted">
            100% Secure Processing · Transparent Allocation
          </span>
        </div>
      </motion.div>

      {/* FIX #2: DonateInMemoryModal — pass donationConfig for dynamic tips */}
      <AnimatePresence>
        {showDonateModal && (
          <DonateInMemoryModal
            isOpen={showDonateModal}
            onClose={() => setShowDonateModal(false)}
            memorial={memorial}
            onSuccess={handleDonationSuccess}
            campaign={activeCampaign}
            feeRate={donationConfig.feeRate}
            tipOptions={donationConfig.tipOptions}
            allowTip={donationConfig.allowTip}
          />
        )}
      </AnimatePresence>

      {/* FIX #1: Campaign Form Modal — properly portaled with correct z-index and scroll */}
      {showCampaignForm && createPortal(
        <AnimatePresence>
          <motion.div
            key="campaign-form-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-accent-navy/70 backdrop-blur-md overflow-y-auto"
            onClick={(e) => { if (e.target === e.currentTarget) setShowCampaignForm(false); }}>
            <div className="min-h-screen flex items-start justify-center p-4 py-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="w-full max-w-2xl"
                onClick={e => e.stopPropagation()}>
                <DonationCampaignForm
                  memorialId={memorial?.id}
                  memorialName={memorial?.name}
                  onSuccess={() => { setShowCampaignForm(false); loadCampaigns(); }}
                  onCancel={() => setShowCampaignForm(false)}
                />
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Donors Modal */}
      {showDonorsModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-[40px] shadow-2xl w-full max-w-md overflow-hidden border border-brand-dark flex flex-col max-h-[80vh]">
            <div className="p-8 border-b border-brand-light flex justify-between items-center shrink-0 bg-brand-light/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent-gold text-white rounded-xl flex items-center justify-center shadow-sm">
                  <SafeIcon icon={FiUsers} />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-accent-navy">Recent Supporters</h3>
                  <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Thank you for your generosity</p>
                </div>
              </div>
              <button onClick={() => setShowDonorsModal(false)} className="p-2 hover:bg-white rounded-full text-text-muted transition-colors">
                <SafeIcon icon={FiX} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-grow space-y-3">
              {MOCK_DONORS.map(donor => (
                <div key={donor.id} className="flex items-center justify-between p-4 bg-white border border-brand-dark rounded-2xl">
                  <div className="flex items-center gap-4">
                    <img src={donor.avatar} alt={donor.name} className="w-12 h-12 rounded-xl object-cover grayscale" />
                    <div>
                      <p className="text-sm font-bold text-accent-navy">{donor.name}</p>
                      <p className="text-[10px] text-text-muted uppercase tracking-widest">{donor.date}</p>
                    </div>
                  </div>
                  <span className="text-sm font-serif text-accent-gold font-bold">{formatCurrency(donor.amount)}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>,
        document.body
      )}
    </>
  );
};

export default DonationProgressWidget;